"use client";

import { Cookie, X } from "lucide-react";
import { useEffect, useState } from "react";
import { getAnalyticsCollector } from "./analytics-collector";
import { Button } from "./ui/button";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [analytics, setAnalytics] = useState<ReturnType<
    typeof getAnalyticsCollector
  > | null>(null);

  useEffect(() => {
    // Initialize analytics collector
    const analyticsInstance = getAnalyticsCollector();
    setAnalytics(analyticsInstance);

    // Check if user has already made a choice
    const consentStatus = localStorage.getItem("cookie-consent");

    if (!consentStatus) {
      const timer = setTimeout(() => {
        setShowIcon(true);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setHasConsent(true);
    }
  }, []);

  const handleAccept = () => {
    if (!analytics) {
      console.error("Analytics not initialized");
      return;
    }

    try {
      analytics.handleAcceptAnalytics(true);
      localStorage.setItem("cookie-consent", "accepted");

      // Hide banner and icon
      setShowBanner(false);
      setShowIcon(false);
      setHasConsent(true);
    } catch (error) {
      localStorage.setItem("cookie-consent", "accepted");
      setShowBanner(false);
      setShowIcon(false);
      setHasConsent(true);
    }
  };

  const handleDecline = () => {
    if (!analytics) {
      return;
    }

    try {
      // Call the analytics API - user declines analytics
      analytics.handleDeclineAnalytics();

      localStorage.setItem("cookie-consent", "declined");

      // Hide banner and icon
      setShowBanner(false);
      setShowIcon(false);
      setHasConsent(true);
    } catch (error) {
      localStorage.setItem("cookie-consent", "declined");
      setShowBanner(false);
      setShowIcon(false);
      setHasConsent(true);
    }
  };

  // Show floating icon if user hasn't accepted/declined yet
  if (showIcon && !showBanner) {
    return (
      <button
        onClick={() => {
          setShowBanner(true);
          setShowIcon(false);
        }}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg shadow-black/20 flex items-center justify-center transition-all duration-200 hover:scale-110"
        title="Cookie Preferences"
      >
        <Cookie className="w-6 h-6" />
      </button>
    );
  }

  // Show banner when user clicks icon or preferences is visited
  if (!showBanner || hasConsent) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50">
      <div className="bg-white rounded-lg shadow-lg shadow-black/20 border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-cyan-50 rounded-full flex items-center justify-center">
              <Cookie className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Cookie Consent
              </h3>
              <p className="text-sm text-gray-500">
                We use cookies to enhance your experience
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowBanner(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          This website uses cookies to improve your browsing experience, analyze
          site traffic, and personalize content. By clicking "Accept All", you
          consent to our use of cookies. You can manage your preferences in
          settings.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleAccept}
            className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            Accept All
          </Button>
          <Button
            onClick={handleDecline}
            variant="outline"
            className="flex-1 border-gray-300 hover:bg-gray-50"
          >
            Decline
          </Button>
          <a href="/cookie-preferences">
            <Button
              variant="ghost"
              className="text-gray-600 hover:text-gray-900"
            >
              Preferences
            </Button>
          </a>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Learn more in our{" "}
            <a
              href="/privacy-policy"
              className="text-cyan-600 hover:text-cyan-700 underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// // For accepting analytics:
// analytics.handleAcceptAnalytics(true); // true = marketing accepted

// // For declining analytics:
// analytics.handleDeclineAnalytics();

// // For custom preferences:
// analytics.handleSavePreferences(preferences);
